import type { LobbyGame } from '../api/getLobby';
import type {
  SurvivorContestDetail,
  SurvivorContestSummary,
  SurvivorEntry,
  SurvivorMyPicks,
  SurvivorPickSummary,
  SurvivorStats,
  SurvivorWeekSummary,
} from '../api/getMyGames';
import { getReservedStatusLabel } from './reservedStatusLabel';

/**
 * One Survivor contest the user actually holds an entry in, assembled for My Games.
 *
 * Survivor exposes no "my games" endpoint, so this is stitched together from three sources:
 * the tenant contest list (`/api/survivor`), the per-contest entry (`/my-picks` — the thing
 * that decides membership at all), and the contest config/week list. The lobby listing is
 * consulted last, for the presentation fields the Survivor API does not carry (prize copy,
 * entry count, play URL) — a contest can be entered by direct link and be absent from the
 * lobby entirely, which is why nothing here depends on finding a lobby row.
 */
export interface SurvivorMyGame {
  pickemId: string;
  title: string;
  sport: string | null;
  startUtc: string | null;
  /** `pickems.pickem_status` — free text, compared case-insensitively throughout. */
  pickemStatus: string | null;
  entry: SurvivorEntry;
  picks: SurvivorPickSummary[];
  weeks: SurvivorWeekSummary[];
  prizeText: string | null;
  entryCount: number | null;
  /**
   * The contest's own link, straight from the backend.
   *
   * Null when the backend could not build one (unknown tenant or game type). The card falls back
   * to deriving a URL from the lobby listing, which only works while a contest of this game type
   * is actually listed — the failure this field exists to remove.
   */
  publicUrl: string | null;
  stats: SurvivorStats | null;
}

export function buildSurvivorMyGames(input: {
  contests: SurvivorContestSummary[];
  myPicksById: Map<string, SurvivorMyPicks>;
  detailById: Map<string, SurvivorContestDetail>;
  statsById?: Map<string, SurvivorStats>;
  lobbyGames: LobbyGame[];
}): SurvivorMyGame[] {
  const { contests, myPicksById, detailById, statsById, lobbyGames } = input;
  const games: SurvivorMyGame[] = [];

  for (const contest of contests) {
    const mine = myPicksById.get(contest.id);
    // No entry — not the user's game. This is the ONLY membership test: the contest list is
    // tenant-wide and includes contests they have never opened.
    if (!mine?.entry) continue;

    const detail = detailById.get(contest.id);
    const lobbyGame = lobbyGames.find(g => g.pickem_id === contest.id);

    games.push({
      pickemId: contest.id,
      title: (detail?.pickem.titleEn || contest.titleEn || 'Survivor').trim(),
      sport: detail?.pickem.sport ?? contest.sport ?? null,
      startUtc: detail?.pickem.start ?? contest.startUtc ?? null,
      pickemStatus: detail?.pickem.status ?? contest.pickemStatus ?? null,
      entry: mine.entry,
      picks: mine.picks ?? [],
      weeks: detail?.weeks ?? [],
      prizeText: lobbyGame?.prize_text_en ?? lobbyGame?.prize_text_fr ?? null,
      entryCount: lobbyGame?.entry_count ?? null,
      publicUrl: contest.publicUrl ?? null,
      stats: statsById?.get(contest.id) ?? null,
    });
  }

  // Soonest first, which is the order the Upcoming and Live tabs want; Recent re-sorts to newest.
  return games.sort((a, b) => toTime(a.startUtc) - toTime(b.startUtc));
}

function toTime(iso: string | null): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Is the CONTEST over? A contest-level fact, independent of how the user's own entry ended —
 * `isSurvivorEntryOver` is what the My Games tabs file a card by.
 *
 * Falls back to the week list because `pickem_status` is free text that a season-long contest
 * is not guaranteed to have been moved to COMPLETE the moment its last week graded.
 */
export function isSurvivorContestComplete(game: SurvivorMyGame): boolean {
  const status = String(game.pickemStatus ?? '').toUpperCase();
  if (status === 'COMPLETE' || status === 'COMPLETED' || status === 'FINAL' || status === '2') {
    return true;
  }
  return game.weeks.length > 0 && game.weeks.every(week => week.status === 'graded');
}

/**
 * Is this contest over *for this user*? What the My Games tabs file a Survivor card by.
 *
 * Elimination is final and Survivor seasons are long: a player knocked out in week 2 of a
 * 17-week contest cannot pick again for months, so leaving their card in Live alongside
 * contests they can still act on reads as an active entry and sends them into the app
 * expecting a pick to make. Recent is where a finished entry belongs, and the card keeps
 * saying which week they went out in.
 *
 * The other game types have no equivalent: a Squares board or a Pick'em round resolves inside
 * a single game, so their card leaves Live within hours either way. This is why the rule is
 * Survivor's alone rather than something applied across My Games.
 *
 * Judged on the entry, not the contest, so a user eliminated from one contest still sees
 * every other entry filed on its own merits.
 */
export function isSurvivorEntryOver(game: SurvivorMyGame): boolean {
  return game.entry.status === 'eliminated' || isSurvivorContestComplete(game);
}

/** The week the contest is standing on: the open one, else the latest one under way. */
export function resolveSurvivorCurrentWeek(game: SurvivorMyGame): SurvivorWeekSummary | null {
  const open = game.weeks.find(week => week.status === 'open');
  if (open) return open;
  const started = [...game.weeks]
    .reverse()
    .find(week => week.status === 'locked' || week.status === 'graded');
  return started ?? game.weeks[0] ?? null;
}

export type SurvivorEntryTone = 'alive' | 'out' | 'reserved';

export interface SurvivorEntryLabel {
  text: string;
  tone: SurvivorEntryTone;
  /** True only for the one state that asks the user to do something right now. */
  emphasised: boolean;
}

/**
 * Plain-text "My Status" line on the Survivor My Games card.
 *
 * A reserved entry (a seat held with no pick ever made) reuses the shared DKPLAT-533 copy so
 * the lobby says the same three things about a reservation whatever the game type. Survivor
 * has no "questions posted" gate of its own — its equivalent of "you can act now" is simply
 * whether a week is open — so that is what maps onto `questionsAvailable`.
 */
export function survivorEntryLabel(game: SurvivorMyGame): SurvivorEntryLabel {
  if (game.entry.status === 'eliminated') {
    const week = game.entry.eliminatedWeekNumber;
    return {
      text: week ? `Eliminated W${week}` : 'Eliminated',
      tone: 'out',
      emphasised: false,
    };
  }

  // `survivor_entries.status` DEFAULTS to `reserved`, so the pick count is what actually
  // separates a held seat from an entry in play — not the status alone.
  if (game.entry.status === 'reserved' && game.picks.length === 0) {
    const hasOpenWeek = game.weeks.some(week => week.status === 'open');
    const hasStartedWeek = game.weeks.some(
      week => week.status === 'locked' || week.status === 'graded'
    );
    const label = getReservedStatusLabel(
      hasStartedWeek && !hasOpenWeek ? 'live' : 'upcoming',
      hasOpenWeek
    );
    return { text: label.text, tone: 'reserved', emphasised: label.emphasised };
  }

  return { text: 'Survived', tone: 'alive', emphasised: false };
}

/** `3/10` — survivors still in over everyone who entered the field. */
export function formatSurvivorFieldCount(game: SurvivorMyGame): string {
  const { stats, entryCount } = game;
  if (stats && stats.total > 0) {
    return `${stats.survived.toLocaleString('en-US')}/${stats.total.toLocaleString('en-US')}`;
  }
  if (typeof entryCount === 'number' && entryCount > 0) {
    return `—/${entryCount.toLocaleString('en-US')}`;
  }
  return '—';
}
